import { MAX_ZNAKOW_TEKSTU } from "./ocenaTypy.js";

/**
 * Parser tekstu rozmowy w ogolnym formacie. Nic tu nie wie o zadnym narzedziu do
 * transkrypcji - to zwykly tekst, ktory ktos wkleil albo wgral jako .txt.
 *
 * Uzywany w DWOCH miejscach tym samym kodem: w formularzu (podglad "rozpoznano N
 * mowcow, M wypowiedzi") i na serwerze (zapis sesji). Dlatego lezy w shared/ i
 * jest czysta funkcja: zero DOM, zero API Node, zero sieci.
 *
 * FORMAT
 *
 *   [czas] Mowca: tekst
 *
 *   - czas jest opcjonalny: mm:ss albo hh:mm:ss, w nawiasach kwadratowych albo
 *     bez (np. "[05:12] Anna: ..." i "05:12 Anna: ..."),
 *   - mowca to krotka etykieta przed PIERWSZYM dwukropkiem w linii, po ktorym
 *     jest bialy znak albo koniec linii ("Anna", "Klient", "Jakub P.", "A"),
 *   - linia bez prefiksu mowcy to ciag dalszy poprzedniej wypowiedzi (sklejany
 *     spacja), takze po pustej linii,
 *   - pusta linia, BOM i znaki konca linii \r sa pomijane,
 *   - KAZDA linia z prefiksem to osobna wypowiedz, takze wtedy, gdy ten sam
 *     mowca mowi kilka razy z rzedu.
 *
 * JAK ODROZNIC MOWCE OD ZWYKLEGO ZDANIA Z DWUKROPKIEM
 *
 * "Uwaga: to wazne" wyglada jak linia mowcy "Uwaga". Dlatego etykieta ma limity
 * ksztaltu (PROGI_PARSERA: do 40 znakow i do 4 slow, musi zawierac litere,
 * bez nawiasow kwadratowych), a za mowce uznajemy ja dopiero wtedy, gdy
 * WYSTEPUJE W TEKSCIE CO NAJMNIEJ DWA RAZY. Etykieta, ktora wystapila raz, nie
 * jest mowca - linia z nia zostaje ciagiem dalszym poprzedniej wypowiedzi, a
 * parser zglasza to w `uwagi` (POJEDYNCZA_ETYKIETA), zeby nie zniknelo to
 * po cichu. Etykiety porownujemy bez rozrozniania wielkosci liter i bialych
 * znakow ("Anna" = "ANNA" = "Anna "), a pokazujemy w pierwszej napotkanej postaci.
 *
 * PROG DLA TEKSTU MIESZANEGO (czesc linii z mowca, czesc bez)
 *
 * Wynik jest PODPISANY tylko wtedy, gdy spelnione sa OBA warunki:
 *   1. rozpoznano co najmniej MIN_MOWCOW (2) mowcow - jeden mowca to nie rozmowa,
 *   2. linii zaczynajacych sie od rozpoznanego mowcy jest albo co najmniej
 *      MIN_LINII_Z_MOWCA_NIEZALEZNIE_OD_UDZIALU (6), albo stanowia one co najmniej
 *      MIN_UDZIAL_LINII_Z_MOWCA (15%) wszystkich niepustych linii. Ten warunek odsiewa
 *      tekst bez podpisow, w ktorym przypadkiem powtarzaja sie dwa naglowki ("Cel: ...",
 *      "Wniosek: ..."), a uznanie ich za mowcow ukrylo by cala reszte tekstu w "ciagach
 *      dalszych". Sam udzial NIE MOZE rozstrzygac, bo zalezy od zawijania: ta sama rozmowa
 *      zawinieta na 80 kolumnach ma 10-14% linii z mowca (podpis stoi tylko w pierwszej
 *      linii wypowiedzi), a niezawinieta - 100%, wiec dluga, twardo lamana rozmowa z
 *      podpisami zostawalaby "tekstem bez podpisow". Dlatego od 6 linii z mowca (po trzy
 *      wypowiedzi na kazdego z dwoch mowcow) udzial nie jest sprawdzany; ponizej chroni
 *      przed naglowkami (dwa powtorzone dwa razy to 4 linie).
 * W kazdym innym przypadku wynik to BEZ_PODPISOW i tekst idzie do podzialu przez
 * model. Pomylka w te strone jest TANIA (zbedny podzial, ktory uzytkownik i tak
 * zatwierdza), pomylka w druga - nie, wiec progi ciagna ku BEZ_PODPISOW.
 *
 * CZASY
 *
 * Wypowiedz ma czas tylko wtedy, gdy WSZYSTKIE wypowiedzi go maja i czasy nie
 * malaja. W kazdym innym przypadku `maCzasy` jest false, a startMs wszystkich
 * wypowiedzi to null (z uwaga CZASY_CZESCIOWE albo CZASY_NIEMONOTONICZNE, jesli
 * czasy byly, ale nie dalo sie im zaufac). Nie zgadujemy czasow, ktorych nie ma.
 *
 * Linia z czasem, ale bez mowcy, NIE jest linia mowcy: zostaje w tekscie jak
 * kazda inna linia ciagu dalszego.
 */

export const PROGI_PARSERA = {
  /** Etykieta mowcy: najwyzej tyle znakow. */
  MAX_ZNAKOW_ETYKIETY: 40,
  /** Etykieta mowcy: najwyzej tyle slow. */
  MAX_SLOW_ETYKIETY: 4,
  /** Etykieta jest mowca, gdy wystepuje w tylu liniach (z prefiksem) albo wiecej. */
  MIN_WYSTAPIEN_ETYKIETY: 2,
  /** Tylu roznych mowcow trzeba, zeby tekst byl rozmowa, a nie monologiem z naglowkami. */
  MIN_MOWCOW: 2,
  /** Jaka czesc niepustych linii musi zaczynac sie od rozpoznanego mowcy (0..1), gdy linii z mowca jest malo. */
  MIN_UDZIAL_LINII_Z_MOWCA: 0.15,
  /**
   * Tyle linii z rozpoznanym mowca wystarczy bez wzgledu na udzial. Udzial zalezy od zawijania
   * dlugich wypowiedzi, wiec nie moze dyskwalifikowac rozmowy, ktora ma dosc wypowiedzi:
   * po trzy na kazdego z MIN_MOWCOW mowcow, i wiecej niz dwa naglowki powtorzone dwa razy.
   */
  MIN_LINII_Z_MOWCA_NIEZALEZNIE_OD_UDZIALU: 6,
  /** Ile uwag o pojedynczych etykietach zwracamy (reszte zlicza `pominietoUwag`). */
  MAX_UWAG: 20,
} as const;

// ---------------------------------------------------------------------------
// Typy wyniku
// ---------------------------------------------------------------------------

export type KodBleduParsera = "TEKST_PUSTY" | "ZLE_KODOWANIE" | "TEKST_ZA_DLUGI";

/** Liczba z odstepami co trzy cyfry, ZWYKLA spacja (toLocaleString wstawia twarda). */
function formatujLiczbe(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** Czytelne komunikaty po polsku, do pokazania przy polu formularza. */
export const KOMUNIKATY_BLEDOW_PARSERA: Record<KodBleduParsera, string> = {
  TEKST_PUSTY: "Tekst jest pusty. Wklej rozmowę albo wybierz plik z tekstem.",
  ZLE_KODOWANIE:
    "Nie da się odczytać tego tekstu. Plik musi być zwykłym plikiem tekstowym w kodowaniu UTF-8 (nie dokumentem Worda ani PDF-em).",
  TEKST_ZA_DLUGI: `Tekst jest za długi. Najdłuższy dozwolony tekst ma ${formatujLiczbe(MAX_ZNAKOW_TEKSTU)} znaków. Podziel rozmowę na części.`,
};

export type PowodBezPodpisow = "BRAK_MOWCOW" | "ZA_MALO_MOWCOW" | "ZA_MALO_LINII_Z_MOWCA";

export type KodUwagiParsera =
  | "POJEDYNCZA_ETYKIETA"
  | "POMINIETY_POCZATEK"
  | "PUSTA_WYPOWIEDZ"
  | "CZASY_CZESCIOWE"
  | "CZASY_NIEMONOTONICZNE";

export interface UwagaParsera {
  kod: KodUwagiParsera;
  /** Numer linii w znormalizowanym tekscie, liczony od 1. */
  linia: number;
  /** Fragment linii (skrocony), zeby uwage dalo sie pokazac bez zagladania w tekst. */
  tekst: string;
}

export interface WypowiedzZTekstu {
  /** Pozycja w rozmowie, liczona od ZERA, jak Utterance.index w bazie. */
  index: number;
  /** Etykieta mowcy w postaci, w jakiej pierwszy raz padla w tekscie. */
  mowca: string;
  /** Czas poczatku w ms. Null, gdy tekst nie ma wiarygodnych czasow (patrz maCzasy). */
  startMs: number | null;
  text: string;
  liczbaSlow: number;
}

export interface MowcaZTekstu {
  etykieta: string;
  liczbaWypowiedzi: number;
  liczbaSlow: number;
}

export interface WynikPodpisany {
  rodzaj: "PODPISANY";
  mowcy: MowcaZTekstu[];
  wypowiedzi: WypowiedzZTekstu[];
  /** Czy startMs jest prawdziwym czasem. false: wszystkie startMs to null. */
  maCzasy: boolean;
  liczbaSlow: number;
  uwagi: UwagaParsera[];
  /** Ile uwag pominieto ponad limit PROGI_PARSERA.MAX_UWAG. */
  pominietoUwag: number;
}

export interface WynikBezPodpisow {
  rodzaj: "BEZ_PODPISOW";
  powod: PowodBezPodpisow;
  /**
   * Tekst po normalizacji (bez BOM, znaki konca linii sprowadzone do \n, bez
   * pustych znakow na koncach). To jest "oryginal" dla podzialu przez model i
   * wartosc, ktora trafia do AnalysisSession.sourceText. Puste wiersze miedzy
   * akapitami zostaja - podpowiadaja modelowi granice wypowiedzi.
   */
  tekst: string;
  liczbaSlow: number;
}

export interface WynikBledu {
  rodzaj: "BLAD";
  kod: KodBleduParsera;
  komunikat: string;
}

export type WynikParsowania = WynikPodpisany | WynikBezPodpisow | WynikBledu;

// ---------------------------------------------------------------------------
// Pomocnicze
// ---------------------------------------------------------------------------

/** Liczba slow liczona tak samo jak w workerze (Utterance.wordCount). */
export function policzSlowa(tekst: string): number {
  return tekst.split(/\s+/).filter(Boolean).length;
}

/**
 * Sprowadza surowy tekst do postaci, na ktorej pracuje parser i podzial:
 * bez BOM, z jednym rodzajem konca linii, bez bialych znakow na koncach calosci.
 */
export function normalizujTekstWejsciowy(surowy: string): string {
  return surowy.replace(/\uFEFF/g, "").replace(/\r\n?/g, "\n").trim();
}

/**
 * Dekoduje bajty pliku jako UTF-8 w trybie RYGORYSTYCZNYM: bajty, ktore nie sa
 * poprawnym UTF-8 (np. plik w starym kodowaniu Windows, dokument Worda), daja
 * ZLE_KODOWANIE zamiast tekstu z "krzaczkami". Uzywane przy pliku .txt, w
 * formularzu i na serwerze tak samo.
 */
export function dekodujTekstUtf8(bajty: Uint8Array): { tekst: string } | { blad: KodBleduParsera } {
  try {
    const tekst = new TextDecoder("utf-8", { fatal: true }).decode(bajty);
    return { tekst };
  } catch {
    return { blad: "ZLE_KODOWANIE" };
  }
}

interface Czas {
  ms: number;
  /** Reszta linii po prefiksie czasu. */
  reszta: string;
}

const RE_CZAS_W_NAWIASACH = /^\[(\d{1,3}):(\d{2})(?::(\d{2}))?\]\s*/;
const RE_CZAS_BEZ_NAWIASOW = /^(\d{1,3}):(\d{2})(?::(\d{2}))?\s+/;

/**
 * Czas z poczatku linii: [mm:ss], [hh:mm:ss] albo to samo bez nawiasow.
 *
 * mm:ss dopuszcza minuty ponad 59 (narzedzia potrafia pokazac "75:30" dla
 * dlugich spotkan); w hh:mm:ss minuty i sekundy musza byc ponizej 60, a sekundy
 * ponizej 60 zawsze. Cos, co wyglada jak czas, ale nim nie jest ("99:99"),
 * NIE jest czasem - linia zostaje bez prefiksu czasu.
 */
function odczytajCzas(linia: string): Czas | null {
  const m = RE_CZAS_W_NAWIASACH.exec(linia) ?? RE_CZAS_BEZ_NAWIASOW.exec(linia);
  if (!m) return null;

  const a = Number(m[1]);
  const b = Number(m[2]);
  const c = m[3] === undefined ? null : Number(m[3]);

  let sekundy: number;
  if (c === null) {
    // mm:ss
    if (b > 59) return null;
    sekundy = a * 60 + b;
  } else {
    // hh:mm:ss
    if (a > 99 || b > 59 || c > 59) return null;
    sekundy = a * 3600 + b * 60 + c;
  }
  return { ms: sekundy * 1000, reszta: linia.slice(m[0].length) };
}

interface Kandydat {
  etykieta: string;
  klucz: string;
  tresc: string;
}

/**
 * Czy linia (bez prefiksu czasu) ma ksztalt "Etykieta: tresc". Dwukropek musi byc
 * PIERWSZYM w linii i miec po sobie bialy znak albo koniec linii - dzieki temu
 * "Godzina 10:30 spotkania", "http://x" i "Anna:tekst" nie sa liniami mowcy.
 */
function odczytajKandydata(reszta: string): Kandydat | null {
  const m = /^([^:]*):(\s.*)?$/.exec(reszta);
  if (!m) return null;

  const etykieta = m[1].replace(/\s+/g, " ").trim();
  if (etykieta.length === 0 || etykieta.length > PROGI_PARSERA.MAX_ZNAKOW_ETYKIETY) return null;
  if (/[[\]]/.test(etykieta)) return null;
  if (!/\p{L}/u.test(etykieta)) return null;
  if (etykieta.split(" ").length > PROGI_PARSERA.MAX_SLOW_ETYKIETY) return null;

  return {
    etykieta,
    klucz: etykieta.normalize("NFC").toLocaleLowerCase("pl"),
    tresc: (m[2] ?? "").trim(),
  };
}

interface LiniaRozpoznana {
  /** Numer linii w znormalizowanym tekscie, od 1. */
  numer: number;
  /** Cala linia po przycieciu bialych znakow. */
  surowa: string;
  czasMs: number | null;
  kandydat: Kandydat | null;
}

function skroc(tekst: string, limit = 120): string {
  return tekst.length > limit ? `${tekst.slice(0, limit)}...` : tekst;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

export function parsujTekst(wejscie: string): WynikParsowania {
  const blad = (kod: KodBleduParsera): WynikBledu => ({
    rodzaj: "BLAD",
    kod,
    komunikat: KOMUNIKATY_BLEDOW_PARSERA[kod],
  });

  if (typeof wejscie !== "string") return blad("TEKST_PUSTY");
  // U+FFFD to znak zastepczy, ktory dekoder wstawia za bajty spoza UTF-8, a NUL
  // to znak pliku binarnego. Tekstu z jednym z nich nie da sie uczciwie odczytac.
  if (wejscie.includes("\uFFFD") || wejscie.includes("\u0000")) return blad("ZLE_KODOWANIE");

  const tekst = normalizujTekstWejsciowy(wejscie);
  if (tekst.length === 0) return blad("TEKST_PUSTY");
  if (tekst.length > MAX_ZNAKOW_TEKSTU) return blad("TEKST_ZA_DLUGI");

  // Pierwszy przebieg: rozbior kazdej niepustej linii na czas i kandydata na mowce.
  const linie: LiniaRozpoznana[] = [];
  const wystapienia = new Map<string, number>();
  tekst.split("\n").forEach((surowa, i) => {
    const przyciete = surowa.trim();
    if (przyciete.length === 0) return;

    const czas = odczytajCzas(przyciete);
    const kandydat = odczytajKandydata(czas ? czas.reszta : przyciete);
    // Linia z czasem, ale bez mowcy, nie jest linia mowcy - czas zostaje w tresci.
    const wiarygodnyCzas = kandydat ? (czas?.ms ?? null) : null;
    if (kandydat) wystapienia.set(kandydat.klucz, (wystapienia.get(kandydat.klucz) ?? 0) + 1);

    linie.push({ numer: i + 1, surowa: przyciete, czasMs: wiarygodnyCzas, kandydat });
  });

  const rozpoznani = new Set(
    [...wystapienia.entries()].filter(([, n]) => n >= PROGI_PARSERA.MIN_WYSTAPIEN_ETYKIETY).map(([k]) => k),
  );

  const linieZMowca = linie.filter((l) => l.kandydat !== null && rozpoznani.has(l.kandydat.klucz)).length;
  const udzial = linie.length === 0 ? 0 : linieZMowca / linie.length;
  const liczbaSlowTekstu = policzSlowa(tekst);

  const bezPodpisow = (powod: PowodBezPodpisow): WynikBezPodpisow => ({
    rodzaj: "BEZ_PODPISOW",
    powod,
    tekst,
    liczbaSlow: liczbaSlowTekstu,
  });

  if (rozpoznani.size === 0) return bezPodpisow("BRAK_MOWCOW");
  if (rozpoznani.size < PROGI_PARSERA.MIN_MOWCOW) return bezPodpisow("ZA_MALO_MOWCOW");
  if (
    linieZMowca < PROGI_PARSERA.MIN_LINII_Z_MOWCA_NIEZALEZNIE_OD_UDZIALU &&
    udzial < PROGI_PARSERA.MIN_UDZIAL_LINII_Z_MOWCA
  ) {
    return bezPodpisow("ZA_MALO_LINII_Z_MOWCA");
  }

  // Drugi przebieg: skladanie wypowiedzi.
  interface Robocza {
    /** Numer linii, w ktorej zaczela sie wypowiedz. */
    numer: number;
    mowca: string;
    czasMs: number | null;
    czesci: string[];
  }
  const robocze: Robocza[] = [];
  const pierwszaPostac = new Map<string, string>();
  const uwagi: UwagaParsera[] = [];
  let pominietoUwag = 0;
  const dodajUwage = (u: UwagaParsera): void => {
    if (uwagi.length < PROGI_PARSERA.MAX_UWAG) uwagi.push(u);
    else pominietoUwag += 1;
  };

  const poczatekBezMowcy: LiniaRozpoznana[] = [];

  for (const linia of linie) {
    const k = linia.kandydat;
    if (k && rozpoznani.has(k.klucz)) {
      if (!pierwszaPostac.has(k.klucz)) pierwszaPostac.set(k.klucz, k.etykieta);
      robocze.push({
        numer: linia.numer,
        mowca: pierwszaPostac.get(k.klucz) as string,
        czasMs: linia.czasMs,
        czesci: k.tresc.length > 0 ? [k.tresc] : [],
      });
      continue;
    }

    // Linia bez rozpoznanego mowcy: ciag dalszy poprzedniej wypowiedzi.
    if (k) dodajUwage({ kod: "POJEDYNCZA_ETYKIETA", linia: linia.numer, tekst: skroc(linia.surowa) });
    const ostatnia = robocze[robocze.length - 1];
    if (ostatnia) ostatnia.czesci.push(linia.surowa);
    else poczatekBezMowcy.push(linia);
  }

  // Tekst sprzed pierwszej wypowiedzi (np. naglowek "Transkrypt") nie nalezy do
  // zadnego mowcy. Nie doklejamy go do pierwszej wypowiedzi, bo wlozylibysmy
  // mowcy w usta slowa, ktorych nie powiedzial. Odpada, ale NIE po cichu.
  if (poczatekBezMowcy.length > 0) {
    dodajUwage({
      kod: "POMINIETY_POCZATEK",
      linia: poczatekBezMowcy[0].numer,
      tekst: skroc(poczatekBezMowcy.map((l) => l.surowa).join(" ")),
    });
  }

  const niepuste = robocze.filter((r) => r.czesci.length > 0);
  for (const pusta of robocze.filter((r) => r.czesci.length === 0)) {
    dodajUwage({ kod: "PUSTA_WYPOWIEDZ", linia: pusta.numer, tekst: pusta.mowca });
  }

  // Wypowiedzi zostaly zlozone z kazdej linii z prefiksem; jesli po odrzuceniu
  // pustych nic nie zostalo, to nie byla rozmowa.
  if (niepuste.length === 0) return bezPodpisow("ZA_MALO_LINII_Z_MOWCA");

  // Czasy: wszystkie albo zadne.
  const zCzasem = niepuste.filter((r) => r.czasMs !== null).length;
  let maCzasy = zCzasem === niepuste.length;
  if (maCzasy) {
    for (let i = 1; i < niepuste.length; i++) {
      if ((niepuste[i].czasMs as number) < (niepuste[i - 1].czasMs as number)) {
        maCzasy = false;
        dodajUwage({ kod: "CZASY_NIEMONOTONICZNE", linia: niepuste[i].numer, tekst: skroc(niepuste[i].czesci[0]) });
        break;
      }
    }
  } else if (zCzasem > 0) {
    dodajUwage({ kod: "CZASY_CZESCIOWE", linia: niepuste[0].numer, tekst: `${zCzasem} z ${niepuste.length} wypowiedzi ma czas` });
  }

  const wypowiedzi: WypowiedzZTekstu[] = niepuste.map((r, index) => {
    const text = r.czesci.join(" ");
    return {
      index,
      mowca: r.mowca,
      startMs: maCzasy ? (r.czasMs as number) : null,
      text,
      liczbaSlow: policzSlowa(text),
    };
  });

  const wgMowcy = new Map<string, MowcaZTekstu>();
  for (const w of wypowiedzi) {
    const wpis = wgMowcy.get(w.mowca) ?? { etykieta: w.mowca, liczbaWypowiedzi: 0, liczbaSlow: 0 };
    wpis.liczbaWypowiedzi += 1;
    wpis.liczbaSlow += w.liczbaSlow;
    wgMowcy.set(w.mowca, wpis);
  }

  // Po odrzuceniu pustych wypowiedzi mowcow moglo zostac mniej niz prog.
  if (wgMowcy.size < PROGI_PARSERA.MIN_MOWCOW) return bezPodpisow("ZA_MALO_MOWCOW");

  return {
    rodzaj: "PODPISANY",
    mowcy: [...wgMowcy.values()],
    wypowiedzi,
    maCzasy,
    liczbaSlow: wypowiedzi.reduce((n, w) => n + w.liczbaSlow, 0),
    uwagi,
    pominietoUwag,
  };
}
